import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAmountInCurrencyFormat, limitNumber } from "../../utils/number";
import AddCustomerModal from "./AddCustomerModal";
import { deleteCustomer } from "../../services/customerService";
import { confirmAction, errorAlert, successAlert } from "../common/Swal";

export default function CustomerTable({
  customers = [],
  setCustomers,
  refreshStats,
  isLoading,
}) {
  const [search, setSearch] = useState("");
  const [editCustomer, setEditCustomer] = useState(null);

  const navigate = useNavigate();

  const handleItemClick = (customer) => {
    sessionStorage.setItem("selectedCustomer", JSON.stringify(customer));
    navigate(`/customer-ledger/${customer._id}`);
  };

  const handleEdit = (e, customer) => {
    e.stopPropagation();
    setEditCustomer(customer);
  };

  const handleDelete = async (e, customer) => {
    e.stopPropagation();

    const confirmDelete = await confirmAction(
      "Are you sure you want to delete this customer?",
      "This action cannot be undone.",
    );

    if (confirmDelete) {
      try {
        // Call API to delete customer
        const response = await deleteCustomer(customer._id);
        if (response.success) {
          // Remove customer from list
          setCustomers((prev) => prev.filter((c) => c._id !== customer._id));
          await refreshStats();
          successAlert("Customer deleted successfully!");
        }
      } catch (err) {
        console.error("Error deleting customer:", err);
        errorAlert(
          err.response?.data?.message ||
            err.message ||
            "Failed to delete customer. Please try again.",
        );
      }
    }
  };

  const filteredCustomers = customers.filter((c) =>
    c.name?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="col-12 mt-3 my-4 py-3 bgTableCustom">
      <div className="table-responsive tableScroll mt-3">
        <table className="table">
          <thead className="thSticky border border-light bgdarkthead">
            <tr>
              <th className="border border-light bgdarkthead">
                <div className="d-flex align-items-center justify-content-center">
                  <input
                    type="search"
                    className="searchBox"
                    placeholder="Search Customer"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />

                  <span className="searchIconBox2">
                    <i className="bi bi-search searchIcon" />
                  </span>
                </div>
              </th>

              <th className="border border-light bgdarkthead">Balance Type</th>
              <th className="border border-light bgdarkthead">Weight</th>
              <th className="border border-light bgdarkthead">Amount</th>

              {/* ✅ NEW COLUMN */}
              <th className="border border-light bgdarkthead text-center">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {filteredCustomers.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center">
                  {isLoading ? "Loading customers..." : "No customers found."}
                </td>
              </tr>
            ) : (
              filteredCustomers.map((customer) => (
                <tr
                  key={customer._id}
                  onClick={() => handleItemClick(customer)}
                  style={{ cursor: "pointer" }}
                >
                  <td>{customer.name}</td>

                  <td className="text-success">
                    <strong>{customer.balanceType}</strong>
                  </td>

                  <td
                    style={{
                      color:
                        customer.weight > 0
                          ? "green"
                          : customer.weight < 0
                            ? "red"
                            : "black",
                    }}
                  >
                    {limitNumber(Math.abs(customer.weight || "0.000"))}
                  </td>

                  <td
                    style={{
                      color:
                        customer.balance > 0
                          ? "green"
                          : customer.balance < 0
                            ? "red"
                            : "black",
                    }}
                  >
                    {getAmountInCurrencyFormat(Math.abs(customer.balance || 0))}
                  </td>

                  {/* ✅ ACTIONS */}
                  <td
                    className="text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i
                      className="bi bi-pencil-square mx-2 text-primary"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => handleEdit(e, customer)}
                    />

                    <i
                      className="bi bi-trash text-danger"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => handleDelete(e, customer)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AddCustomerModal
        show={editCustomer !== null}
        customer={editCustomer}
        isEdit={true}
        onClose={() => setEditCustomer(null)}
        onSave={async (updatedCustomer) => {
          console.log("Updated Customer:", updatedCustomer);
          // Update the customer in the list
          const updatedCustomers = customers.map((c) =>
            c._id === updatedCustomer._id ? updatedCustomer : c,
          );
          setCustomers(updatedCustomers);
          setEditCustomer(null);
          await refreshStats();
        }}
      />
    </div>
  );
}
